/**
 * Deduplicates identical in-flight catalog queryEntities calls to prevent
 * multiple simultaneous identical HTTP requests to /api/catalog/query/by-query.
 *
 * This patches CatalogClient.prototype.queryEntities once at startup.
 * Identical requests (same serialized params) that arrive while the first is
 * still in-flight will receive the same Promise, collapsing them into a
 * single network call.
 */
import { CatalogClient } from '@backstage/catalog-client';

const inFlight = new Map<string, Promise<any>>();

const original = CatalogClient.prototype.queryEntities;

CatalogClient.prototype.queryEntities = function patchedQueryEntities(
  request?: Parameters<typeof original>[0],
  options?: Parameters<typeof original>[1],
): ReturnType<typeof original> {
  const key = stableStringify({ request, options });

  const existing = inFlight.get(key);
  if (existing) {
    return existing as ReturnType<typeof original>;
  }

  const promise = original.call(this, request, options) as ReturnType<
    typeof original
  >;
  inFlight.set(key, promise);

  promise
    .finally(() => {
      inFlight.delete(key);
    })
    .catch(() => {
      // Ignore — already handled by the caller
    });

  return promise;
};

/**
 * Serializes any value to a deterministic string for deduplication keys.
 * 
 * Key differences from JSON.stringify:
 * - Handles undefined, functions, and symbols (returns string representations)
 * - Recursively stable: object properties always sorted by key name
 * - Ensures identical values always produce identical strings
 * 
 * Used to deduplicate in-flight catalog API requests by request params.
 */
function stableStringify(value: unknown): string {
  // Handle special types that JSON.stringify can't serialize
  if (value === undefined) return 'undefined';
  if (typeof value === 'function') return 'function';
  if (typeof value === 'symbol') return `symbol(${String(value)})`;

  // Serialize arrays recursively
  if (Array.isArray(value)) {
    const items = value.map(stableStringify).join(',');
    return `[${items}]`;
  }

  // Serialize objects with sorted keys for stable output
  if (value !== null && typeof value === 'object') {
    const sortedEntries = Object.entries(value as Record<string, unknown>)
      // Use simple lexical comparison (< and >) for deterministic ordering.
      // localeCompare is locale-dependent and would cause different key ordering
      // on different systems/environments, breaking deduplication consistency.
      .sort(([keyA], [keyB]) => keyA < keyB ? -1 : keyA > keyB ? 1 : 0);
    
    const pairs = sortedEntries.map(
      ([key, val]) => `"${key}":${stableStringify(val)}`
    );
    return `{${pairs.join(',')}}`;
  }

  // Fall back to JSON.stringify for primitives (null, boolean, number, string)
  return JSON.stringify(value) ?? 'null';
}
